import { useEffect, useState, useRef } from 'react';
import { Toast as ToastType } from '@/shared/contexts/ToastContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, AlertTriangle, Check, Info } from 'lucide-react';

interface ToastProps {
  toast: ToastType;
  onRemove: () => void;
}

const Toast = ({ toast, onRemove }: ToastProps) => {
  const [visible, setVisible] = useState(true);
  const progressRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const toastDuration = toast.duration;
  const animationDuration = 300; // Exit animation duration in ms

  // Start exit animation before removing from DOM
  useEffect(() => {
    // Initialize start time when effect runs
    startTimeRef.current = Date.now();
    const visibleDuration = toastDuration - animationDuration;

    const timer = setTimeout(() => {
      setVisible(false);
    }, visibleDuration);

    // Update progress bar - this will run for the full visible duration
    const updateProgressBar = () => {
      if (!progressRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      // Make sure progress bar finishes exactly when the visible duration ends
      const remaining = Math.max(0, visibleDuration - elapsed);
      const progress = (remaining / visibleDuration) * 100;

      progressRef.current.style.width = `${progress}%`;

      if (elapsed < visibleDuration) {
        animationFrameRef.current = requestAnimationFrame(updateProgressBar);
      } else if (progressRef.current) {
        // Ensure we set to exactly 0% at the end
        progressRef.current.style.width = '0%';
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateProgressBar);

    return () => {
      clearTimeout(timer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [toastDuration]);

  // Once exit animation completes, call onRemove
  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(onRemove, animationDuration);
      return () => clearTimeout(timer);
    }
    return () => {}; // Return empty cleanup function when visible is true
  }, [visible, onRemove, animationDuration]);

  // Icone + cor de destaque por tipo — tema escuro do Liberty
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return { icon: <Check className="w-5 h-5" />, accent: '#22c55e' };
      case 'error':
        return { icon: <AlertCircle className="w-5 h-5" />, accent: '#ef4444' };
      case 'warning':
        return { icon: <AlertTriangle className="w-5 h-5" />, accent: '#f59e0b' };
      case 'info':
      default:
        return { icon: <Info className="w-5 h-5" />, accent: '#00e1d7' };
    }
  };

  const { icon, accent } = getToastStyles();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          className="rounded-lg flex flex-col w-full max-w-md mx-auto overflow-hidden md:mx-0 z-201 border backdrop-blur-md"
          style={{
            minWidth: '280px',
            maxWidth: '100%',
            background: 'rgba(13, 20, 27, 0.97)',
            borderColor: `${accent}55`,
            boxShadow: `0 10px 34px rgba(0,0,0,0.55), 0 0 18px ${accent}22`,
          }}
          role="alert"
        >
          <div className="p-3 md:p-3 flex items-center gap-2 md:gap-3">
            <div
              className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ background: `${accent}1f`, color: accent }}
            >
              {icon}
            </div>
            <p className="text-sm md:text-sm font-medium flex-1 wrap-break-words text-white/90">
              {toast.message}
            </p>
          </div>

          {/* Progress indicator */}
          <div className="h-1 w-full" style={{ background: `${accent}1a` }}>
            <div
              ref={progressRef}
              className="h-full"
              style={{ width: '100%', transition: 'width linear', background: accent }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
